import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLocation } from 'react-router-dom';
import { useDownloadAssets } from '../DownloadAssetsContext';
import { uploadFiless } from 'components/Dam/Assets';
import { MODULE_IDS, useHasAccess } from 'actions/userActions';

const GlobalDropzoneContext = createContext(null);

/* ---------------- ALLOWED DAM ROUTES ---------------- */

const ALLOWED_DAM_ROUTES = ['/dam/v2/assets'];

export const GlobalDropzoneProvider = ({ children }) => {
  const location = useLocation();
  const { UploadFiles: UploadFilesContext } = useDownloadAssets();
  const [isDragging, setIsDragging] = useState(false);
  const hasDAMAccess = useHasAccess(MODULE_IDS.DAM);
  const ignoreNextDrop = useRef(false);

  // Whether the folder currently on screen accepts uploads from this user. Innerfolder
  // owns this call (it is what hides the Upload button) and pushes it down here, so a
  // page-level drop can never upload somewhere the Upload button refuses to.
  // Defaults to true for the assets root, where Innerfolder is not mounted.
  const [canUploadHere, setCanUploadHere] = useState(true);

  // A component that handles drops anywhere on the page for itself - the Search
  // By Image box - claims them while it is open, and the page-level uploader
  // stands down. Without this one dropped image is both searched for and
  // uploaded as a new asset. Counted, so overlapping claims do not undo each
  // other when the first one is released.
  const [pageDropClaims, setPageDropClaims] = useState(0);

  const claimPageDrop = useCallback((claimed) => {
    setPageDropClaims((count) => Math.max(0, count + (claimed ? 1 : -1)));
  }, []);

  /* ---------- Helpers ---------- */

  const isDamRoute = useMemo(() => {
    return ALLOWED_DAM_ROUTES.some((route) => location.pathname.startsWith(route)) && hasDAMAccess;
  }, [location.pathname, hasDAMAccess]);

  const canDropOnPage = isDamRoute && canUploadHere && pageDropClaims === 0;

  const getUploadFolderId = (path) => {
    if (!path) return null;

    const parts = path.split('/').filter(Boolean);
    const last = parts[parts.length - 1];

    // if ends with "assets" → root
    if (last === 'assets') return null;

    // otherwise last segment is folder id
    return last;
  };

  /* ---------- Dropzone ---------- */

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: !canDropOnPage, // 🔥 IMPORTANT
    onDragEnter: () => canDropOnPage && setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    onDrop: (acceptedFiles) => {
      if (!canDropOnPage) return;
      setIsDragging(false); // <-- move this to the top

      if (ignoreNextDrop.current) {
        ignoreNextDrop.current = false;
        return;
      }

      setIsDragging(false);
      const folderId = getUploadFolderId(location.pathname);
      uploadFiless(UploadFilesContext, acceptedFiles, folderId);
    },
  });

  return (
    <GlobalDropzoneContext.Provider
      value={{
        isDragActive,
        isDragging,
        isDamRoute,
        ignoreNextDrop,
        setIsDragging,
        setCanUploadHere,
        claimPageDrop,
      }}
    >
      <div {...(canDropOnPage ? getRootProps() : {})} style={{ minHeight: '100vh' }}>
        {children}

        {/* -------- Global Overlay -------- */}
        {isDragging && canDropOnPage && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(33, 207, 214, 0.35)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <h2 style={{ color: '#fff', fontSize: 26 }}>Drop files to upload</h2>
          </div>
        )}
      </div>
    </GlobalDropzoneContext.Provider>
  );
};

export const useGlobalDropzone = () => useContext(GlobalDropzoneContext);
